// THE CODING LANE'S THREE EVENTS: the manifest it surveys before dispatch, the editor
// fan-out once the round settles, and the bounded second wave after the gate went red.
// One module because they are one lane's story and share its vocabulary — the editor
// failure kinds and the unrouted handoff are read by all three.
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import {
  count,
  HIVE_EVENT_MAX_ITEMS,
  HIVE_EXCERPT_MAX_CHARS,
  HIVE_PATH_MAX_CHARS,
  line,
  repoPath,
} from './bounds';
import { HiveEvent } from './kind';
import { HiveNoWorkSchema } from './no-work';
import { HiveRoundPayloadSchema } from './round';
import { HiveSteerEntrySchema } from './steer';

/**
 * THE SURVEYED MANIFEST, emitted before a single editor is dispatched
 * (`apps/runner/src/modes/worker/team/session/events.ts` `manifestEvent`).
 *
 * NOT a {@link HiveRoundPayloadSchema}: no round has run. What it records is the lead's
 * PARTITION (rule 1) and the two facts that are only useful before dispatch — where each
 * leaf can be reached (rule 9) and which of them were named a scoped check (rule 6).
 *
 * COUNTS AND DECLARED PATHS ONLY. A file's `purpose`, the coordinator's `sharedContext`
 * and an editor's scoped command are model prose written over a customer's checkout, and
 * the audit chain is not where repo content belongs. The declared PATHS ride because they
 * are the load-bearing claim: "we said we would write exactly these".
 */
export const TeamManifestPayloadSchema = z.object({
  event: z.literal(HiveEvent.TeamManifest),
  /** Files the coordinator declared. */
  files: count().describe('hive.md rule 1: files the lead partitioned.'),
  /** The declared paths themselves, capped. */
  paths: z
    .array(repoPath())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'hive.md rule 1: the declared paths — the claim a later guard grades the tree against.',
    ),
  paths_truncated: count().describe('paths beyond the cap. A shortened audit line says so.'),
  /** Editors the fan-out will really run — asked of the planner, never re-derived. */
  editors: count().describe(
    'hive.md rule 5: editors the fan-out will run, after the floor collapses a one-tag manifest.',
  ),
  /** Acceptance criteria the coordinator wrote. */
  acceptance: count().describe('acceptance criteria declared for the assignment.'),
  /** Editors handed an explicit scoped command (or the literal `none`) by the lead. */
  scoped_checks: count().describe(
    'hive.md rule 6: editors the lead named a check for. `editors` is the denominator; the command never rides.',
  ),
  /** Files declaring what they change that something ELSE reads. */
  seam: count().describe(
    'hive.md rule 4: files declaring a seam (`{change, readers}`). Disjoint paths are necessary and insufficient.',
  ),
  /** Premises of the assignment the coordinator disproved on the way to this manifest. */
  falsified: count().describe(
    'hive.md rule 11: premises the coordinator disproved. The text rides the editor briefs.',
  ),
  /** Cross-file obligations the coordinator surfaced — rule 10's denominator. */
  handoffs: count().describe(
    'hive.md rule 10: obligations surfaced. What became of each is `team_fanout`, not this event.',
  ),
  /** Where each planned editor can be reached while it works. */
  steer: z
    .array(HiveSteerEntrySchema)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'hive.md rule 9: address -> editor, on the LAST event before dispatch. Steering is only useful before they finish.',
    ),
  steer_truncated: count().describe('addresses beyond the cap. Every editor is still reachable.'),
  /** Bytes of shared context, never the context. */
  shared_context_bytes: count().describe(
    'size of the coordinator prose handed to every editor. The bytes, never the text.',
  ),
  /**
   * One outcome label per corrected re-ask (`schema_echo`, `schema_invalid`, …), plus
   * `no_work_beside_plan` for the one repair the box makes with no re-ask (a plan's stray
   * `noWork` dropped, `apps/runner` `manifest/prepare.ts`). Free text on the wire.
   */
  corrected: z
    .array(line())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'labels of the coordinator answers that were corrected: each re-ask, and no_work_beside_plan for a dropped noWork. A repair that FIRED and one that WORKED are otherwise the same row.',
    ),
  corrected_total: count().describe('corrections, including any beyond the cap.'),
  /** Field NAMES the harness had to trim, never the trimmed value. */
  prose_trimmed: z
    .array(line())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('field names whose prose was trimmed. The value is repo content and never rides.'),
  prose_trimmed_total: count().describe('fields trimmed, including any beyond the cap.'),
  /**
   * THE ASSIGNMENT REFUSED WHOLE (`hive.md` rule 12). `null` is a coordinator that accepted
   * the work — a value it WROTE, not a field it lacked.
   *
   * Nullable and not optional on purpose (rule 3 in `../hive-event.ts`'s header): rule 12's whole point is
   * that a measured refusal is a successful return, and a return that is indistinguishable
   * from a producer that never existed is not a return at all.
   */
  no_work: HiveNoWorkSchema.nullable().describe(
    'hive.md rule 12: the whole assignment refused, measured first. null = accepted.',
  ),
});
export type TeamManifestPayload = z.infer<typeof TeamManifestPayloadSchema>;

// ─── coding lane: the fan-out ────────────────────────────────────────────────

/** Why an editor did not finish. Each is a distinct thing for a human to do next. */
export const HiveEditorFailure = {
  /** Its paths overlap a sibling's — the manifest is wrong, and it never spawned. */
  ClaimConflict: 'claim_conflict',
  /** The box drained before it started — no work was attempted. */
  Aborted: 'aborted',
  /** It ran and exited non-zero. */
  Exit: 'exit',
  /** It ran past the ceiling and was reaped. */
  Timeout: 'timeout',
  /** It could not be run at all (spawn threw, seam rejected). */
  Error: 'error',
} as const;
export type HiveEditorFailure = (typeof HiveEditorFailure)[keyof typeof HiveEditorFailure];

/**
 * One editor that did not come back. Every conditional fact is NULLABLE rather than
 * absent: a `null` exit code is an editor that never reached one, which is a different
 * fact from a field a retired producer did not write, and the read seam must not have to
 * guess which it is holding.
 */
export const TeamEditorFailureSchema = z.object({
  id: line().describe('the editor id — model-authored, which is why it is not the address'),
  failure: z
    .enum(Object.values(HiveEditorFailure))
    .describe('hive.md rule 2: which return path failed. Each names a different next action.'),
  reason: line().describe('one bounded line naming the failure'),
  paths: z
    .array(repoPath())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('the paths this editor was responsible for — the files its failure failed'),
  /** `null` when it never reached one (a claim conflict, a drain, a spawn that threw). */
  exit_code: z.number().int().nullable().describe('exit code, or null if it never reached one'),
  /** Redacted tail of the child output. `null` when there was none. */
  excerpt: z
    .string()
    .max(HIVE_EXCERPT_MAX_CHARS)
    .nullable()
    .describe('redacted tail of the output. Null = there was none; the bytes never ride.'),
  /** Box-local transcript PATH, never the bytes. `null` when nothing was written. */
  transcript: z
    .string()
    .max(HIVE_PATH_MAX_CHARS)
    .nullable()
    .describe(
      'box-local transcript path — what turns "editor b failed" into something a human can open',
    ),
  transcript_bytes: z.number().int().nonnegative().nullable().describe('size of that transcript'),
  transcript_error: z
    .string()
    .max(HIVE_EXCERPT_MAX_CHARS)
    .nullable()
    .describe('why the transcript write was refused. Null and a null path are DIFFERENT faults.'),
});
export type TeamEditorFailure = z.infer<typeof TeamEditorFailureSchema>;

/** One cross-file obligation that reached no editor's brief — rule 10's silent drop, named. */
export const HiveUnroutedHandoffSchema = z.object({
  for_path: repoPath().describe('the file the work belongs to — a schema-validated repo path'),
  why: line(HIVE_EXCERPT_MAX_CHARS).describe(
    'hive.md rule 10: what must happen. REDACTED manifest prose — the one place model text reaches this chain.',
  ),
});
export type HiveUnroutedHandoff = z.infer<typeof HiveUnroutedHandoffSchema>;

/**
 * THE CODING FAN-OUT'S RESULT (`apps/runner/src/modes/worker/team/fanout.ts`
 * `fanOutEvent`). Emitted whether or not an editor failed, because "we dispatched N
 * editors over these paths" is itself the audit claim.
 *
 * Measured baseline this contract exists to move (prod, 30 days to 2026-09-02): 258
 * `team_fanout` rows, **243 of them dispatched exactly ONE editor** — rule 5's
 * strictly-worse-than-nothing case — with `lead_idle_ms === round_ms` on every single one.
 */
export const TeamFanOutPayloadSchema = HiveRoundPayloadSchema.extend({
  event: z.literal(HiveEvent.TeamFanOut),
  /** The concurrency ceiling this box was configured with (`DZ_TEAM_MAX_EDITORS`). */
  max_editors: z
    .number()
    .int()
    .min(1)
    .describe(
      'hive.md rule 5: the ceiling. `dispatched` at the ceiling is a different story from 1.',
    ),
  /**
   * Editor ATTEMPTS spent across the fan-out — at least one per dispatched editor.
   * `attempts === dispatched` with `failed > 0` is a fan-out with no retry ladder, which
   * is what prod measured: 15 failed editors, 0 retried. Producer: plan 102 slice 02.
   */
  attempts: count().describe(
    'hive.md rule 2: attempts spent, >= `dispatched`. Equal to it with failures = no retry ladder.',
  ),
  editor_failures: z
    .array(TeamEditorFailureSchema)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 2: WHY each editor died. Capped; `failed` above is the true count.'),
  editor_failures_truncated: count().describe('failures beyond the cap'),
  /** Paths left unwritten because the editor that owned them failed. */
  failed_paths: z
    .array(repoPath())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 1: paths the partition promised and this fan-out did not write'),
  failed_paths_truncated: count().describe('failed paths beyond the cap'),
  /** Transcripts on disk for this fan-out, across every editor. */
  transcripts: count().describe(
    'transcripts written. Zero with editors dispatched is a store problem.',
  ),
  transcript_errors: count().describe(
    'transcript writes REFUSED. "Nobody wrote one" and "the write failed" call for opposite fixes.',
  ),
  /**
   * The prose of the handoffs that reached nobody. `handoffs_unrouted` on the round is the
   * COUNT; this is the text, and it is the only place it can land — a routed handoff was
   * written into an owning editor's brief, so its text is not lost, while an unrouted one
   * reached no brief at all and letting it stop here is exactly the silent drop rule 10
   * exists to forbid.
   */
  handoffs_unrouted_prose: z
    .array(HiveUnroutedHandoffSchema)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 10: the unrouted handoffs themselves. Empty when there were none.'),
  handoffs_unrouted_prose_truncated: count().describe('unrouted handoffs beyond the cap'),
});
export type TeamFanOutPayload = z.infer<typeof TeamFanOutPayloadSchema>;

// ─── coding lane: the fix wave (plan 102 slice 08) ───────────────────────────

/**
 * What opened a second editor wave. ONE member today and it is deliberately closed: the
 * gate going red with a tail that names manifest paths is EDIT fault, and it is the only
 * thing a fix wave may fire on.
 *
 * `no_verdict`, `no_gate`, `timed_out` and `killed_by` are BOX faults
 * (`box-cannot-run-the-repo-gate`) — a wave fired on one of those re-edits correct code
 * against a gate the box could never run, which is why they are not members here rather
 * than members the producer happens not to use.
 */
export const HiveFixWaveTrigger = {
  /** The repo's own gate exited non-zero with a tail naming manifest paths. */
  Red: 'red',
} as const;
export type HiveFixWaveTrigger = (typeof HiveFixWaveTrigger)[keyof typeof HiveFixWaveTrigger];

/**
 * A BOUNDED SECOND EDITOR WAVE after the gate went red.
 *
 * PRODUCER: `apps/runner/src/modes/worker/team/fix-wave.ts` `fixWaveEvent` (plan 102 slice
 * 08), pinned BY NAME in `hive-event.test.ts` — the guard that used to assert this shape had
 * no emit site anywhere is now the assertion that it has exactly that one, so a second
 * emitter of this event is as loud as none.
 *
 * The funnel it exists to move (prod, 30 days to 2026-09-02): 240 `team_verify` events,
 * 121 of them red, and 10 sessions reaching `finished`. Re-measured over the 30 days to
 * 2026-09-03 as the producer landed: 1,067 sessions opened, 20 finished, 175 red gates.
 * The single biggest loss in the lane is a red gate with nobody sent back at it.
 */
export const FixWavePayloadSchema = HiveRoundPayloadSchema.extend({
  event: z.literal(HiveEvent.TeamFixWave),
  trigger: z
    .enum(Object.values(HiveFixWaveTrigger))
    .describe('what opened the wave. Closed: a box fault is never a reason to re-edit.'),
  /** Which wave this is. Never 1 — wave 1 is the fan-out, and it has its own event. */
  wave: z
    .number()
    .int()
    .min(2)
    .describe('the wave number. >= 2 by construction; wave 1 is `team_fanout`.'),
  /** The manifest paths the gate's tail actually named. */
  failing_paths: z
    .array(repoPath())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'manifest paths the red tail named — what sizes the wave. Empty means no wave was owed.',
    ),
  failing_paths_truncated: count().describe('failing paths beyond the cap'),
  /** Per-test failures the gate printed by title and placed in no file. */
  unplaced_failures: z
    .array(line(HIVE_EXCERPT_MAX_CHARS))
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'failing test TITLES the red tail named with no file — routed to the minted editor by title. Empty when there were none.',
    ),
  unplaced_failures_truncated: count().describe('unplaced failures beyond the cap'),
});
export type FixWavePayload = z.infer<typeof FixWavePayloadSchema>;
